import type { Annotation } from 'api';
import { useCallback, useMemo, useState } from 'react';

import { IGNORE_BALE_LABELS } from '../../constants';
import { getUniqueSegmentationCategories } from '../../utils/formatAnnotations/formatAnnotations';

interface UseAnnotationsReturn {
  selectedAnnotation: Annotation[] | undefined;
  handleImageChange: (annotations: Annotation[] | undefined) => void;
  hiddenCategories: string[];
  handleHideCategory: (label: string) => void;
  handleToggleLegend: () => void;
  showLegend: boolean;
  showSidebar: boolean;
}

export const useAnnotations = ({ initialAnnotations }: { initialAnnotations?: Annotation[] }): UseAnnotationsReturn => {
  const [selectedAnnotation, setSelectedAnnotation] = useState<Annotation[] | undefined>(initialAnnotations);
  const [hiddenCategories, setHiddenCategories] = useState<string[]>([]);
  const [showLegend, setShowLegend] = useState<boolean>(true);

  const allSegmentationCategories = useMemo(
    () => getUniqueSegmentationCategories(selectedAnnotation || []),
    [selectedAnnotation]
  );

  const handleHideCategory = useCallback((label: string) => {
    setHiddenCategories((prev) =>
      prev.includes(label) ? prev.filter((category) => category !== label) : [...prev, label]
    );
  }, []);

  const hideAllCategories = useCallback(
    (annotations?: Annotation[]) => {
      const categories = annotations ? getUniqueSegmentationCategories(annotations) : allSegmentationCategories;

      setHiddenCategories(Array.from(categories).map((category) => category[0]));
    },
    [allSegmentationCategories]
  );

  const showAllCategories = useCallback(() => {
    setHiddenCategories([]);
  }, []);

  const handleToggleLegend = useCallback(() => {
    setShowLegend((prev) => {
      const newShowLegend = !prev;
      if (!newShowLegend) {
        hideAllCategories();
      } else {
        showAllCategories();
      }
      return newShowLegend;
    });
  }, [hideAllCategories, showAllCategories]);

  const handleImageChange = useCallback(
    (annotations: Annotation[] | undefined) => {
      setSelectedAnnotation(annotations);
      if (!showLegend && annotations) {
        hideAllCategories(annotations);
      }
    },
    [showLegend, hideAllCategories]
  );

  const showSidebar = Boolean(
    selectedAnnotation?.length &&
      !selectedAnnotation.every((annotation) => IGNORE_BALE_LABELS.includes(annotation.label))
  );

  return {
    handleHideCategory,
    handleImageChange,
    handleToggleLegend,
    hiddenCategories,
    selectedAnnotation,
    showLegend,
    showSidebar,
  };
};
